import React, { useState, useEffect, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import TextField from '@mui/material/TextField';

import PropTypes from 'prop-types';

import { endOfDay, endOfWeek, startOfDay, addDays, format } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { useStyles } from './TempClosingHours.style';

function TempClosingHoursAddNew({ onClose, onCreate, timezone }) {
  const classes = useStyles();
  const [commonSelected, setCommonSelected] = useState(null);

  // startPeriod.value and endPeriod.value are always true UTC timestamps.
  // All display is done by converting through utcToZonedTime(value, timezone).
  const [startPeriod, setStartPeriod] = useState(null);
  const [endPeriod, setEndPeriod] = useState(null);

  const toZoned = (utcDate) => utcToZonedTime(utcDate, timezone);

  // Build a UTC timestamp by treating (year, month, date, hours, minutes) as
  // restaurant-local time, regardless of the user's browser timezone.
  const naiveToUtc = (y, mo, d, h, mi) =>
    zonedTimeToUtc(new Date(y, mo, d, h, mi, 0, 0), timezone);

  const toTimeString = (utcDate) => {
    if (!utcDate) return '';
    const zoned = toZoned(utcDate);
    return `${String(zoned.getHours()).padStart(2, '0')}:${String(
      zoned.getMinutes()
    ).padStart(2, '0')}`;
  };

  const onStartDateChange = (newDate) => {
    if (newDate) {
      setCommonSelected(null);
      const existingZoned = startPeriod
        ? toZoned(startPeriod.value)
        : toZoned(new Date());
      setStartPeriod({
        label: '',
        value: naiveToUtc(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          existingZoned.getHours(),
          existingZoned.getMinutes()
        ),
      });
    }
  };

  const onStartTimeChange = (e) => {
    const timeStr = e.target.value;
    if (!timeStr) return;
    const [hours, minutes] = timeStr.split(':').map(Number);
    setCommonSelected(null);
    const existingZoned = startPeriod
      ? toZoned(startPeriod.value)
      : toZoned(new Date());
    setStartPeriod({
      label: '',
      value: naiveToUtc(
        existingZoned.getFullYear(),
        existingZoned.getMonth(),
        existingZoned.getDate(),
        hours,
        minutes
      ),
    });
  };

  const onEndDateChange = (newDate) => {
    if (newDate) {
      setCommonSelected(null);
      const existingZoned = endPeriod
        ? toZoned(endPeriod.value)
        : toZoned(new Date());
      setEndPeriod({
        label: '',
        value: naiveToUtc(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          existingZoned.getHours(),
          existingZoned.getMinutes()
        ),
      });
    }
  };

  const onEndTimeChange = (e) => {
    const timeStr = e.target.value;
    if (!timeStr) return;
    const [hours, minutes] = timeStr.split(':').map(Number);
    setCommonSelected(null);
    const existingZoned = endPeriod
      ? toZoned(endPeriod.value)
      : toZoned(new Date());
    setEndPeriod({
      label: '',
      value: naiveToUtc(
        existingZoned.getFullYear(),
        existingZoned.getMonth(),
        existingZoned.getDate(),
        hours,
        minutes
      ),
    });
  };

  const onCommonSelected = (value) => {
    setCommonSelected(value);
    const restaurantNow = toZoned(new Date());

    let startUtc;
    let endUtc;

    if (value === 'today') {
      startUtc = new Date();
      endUtc = zonedTimeToUtc(endOfDay(restaurantNow), timezone);
    } else if (value === 'tomorrow') {
      const tomorrow = addDays(restaurantNow, 1);
      startUtc = zonedTimeToUtc(startOfDay(tomorrow), timezone);
      endUtc = zonedTimeToUtc(endOfDay(tomorrow), timezone);
    } else if (value === 'thisweek') {
      startUtc = new Date();
      endUtc = zonedTimeToUtc(endOfWeek(restaurantNow), timezone);
    } else if (value === 'nextweek') {
      const nextWeekStart = addDays(endOfWeek(restaurantNow), 1);
      startUtc = zonedTimeToUtc(startOfDay(nextWeekStart), timezone);
      endUtc = zonedTimeToUtc(endOfWeek(nextWeekStart), timezone);
    }

    setStartPeriod({ label: '', value: startUtc });
    setEndPeriod({ label: '', value: endUtc });
  };

  useEffect(() => {
    if (startPeriod === null || endPeriod === null) {
      onCommonSelected('today');
    }
  }, []);

  const periodSummaryText = useMemo(() => {
    if (startPeriod !== null && endPeriod !== null) {
      const startZoned = toZoned(startPeriod.value);
      const endZoned = toZoned(endPeriod.value);
      return `${format(startZoned, 'MM/dd/yyyy hh:mm a')} - ${format(
        endZoned,
        'MM/dd/yyyy hh:mm a'
      )}`;
    }
    return null;
  }, [startPeriod, endPeriod]);

  return (
    <Box className={classes.presetHoursContainer}>
      <Box className={classes.presetHoursHeader}>
        <Typography variant="h4">Common Intervals</Typography>
      </Box>
      <Box className={classes.presetHoursCommonButtons}>
        <Button
          data-test-id="Wd2pWfxIAp794jLL1TkWR"
          variant={commonSelected === 'today' ? 'contained' : 'outlined'}
          onClick={() => onCommonSelected('today')}
          className={classes.presetHoursButton}
          disabled={commonSelected === 'today'}
        >
          Today
        </Button>
        <Button
          data-test-id="kjsGRrDYn0Jlb1KTXiUh3"
          variant={commonSelected === 'tomorrow' ? 'contained' : 'outlined'}
          onClick={() => onCommonSelected('tomorrow')}
          className={classes.presetHoursButton}
          disabled={commonSelected === 'tomorrow'}
        >
          Tomorrow
        </Button>
        <Button
          data-test-id="dhABZbY7w6-RO5W5dITE0"
          variant={commonSelected === 'thisweek' ? 'contained' : 'outlined'}
          onClick={() => onCommonSelected('thisweek')}
          className={classes.presetHoursButton}
          disabled={commonSelected === 'thisweek'}
        >
          This Week
        </Button>
        <Button
          data-test-id="wkRX9NdoW50HLNPfMHetK"
          variant={commonSelected === 'nextweek' ? 'contained' : 'outlined'}
          onClick={() => onCommonSelected('nextweek')}
          className={classes.presetHoursButton}
          disabled={commonSelected === 'nextweek'}
        >
          Next Week
        </Button>
      </Box>
      <Box className={classes.presetHoursHeader}>
        <Typography variant="h4">Select Closing Hours</Typography>
        <Typography variant="h5">
          What times will the restaurant be closed?
        </Typography>
        <Box className={classes.presetHourSummaryBox}>
          <Box className={classes.presetHourSummaryBoxDateBox}>
            <Typography variant="h4" sx={{ paddingBottom: '10px' }}>
              From
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <DesktopDatePicker
                value={startPeriod ? toZoned(startPeriod.value) : null}
                onChange={onStartDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
              <TextField
                type="time"
                value={toTimeString(startPeriod?.value)}
                onChange={onStartTimeChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Box>
          </Box>
          <Box className={classes.presetHourSummaryBoxDateBox}>
            <Typography variant="h4" sx={{ paddingBottom: '10px' }}>
              To
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <DesktopDatePicker
                value={endPeriod ? toZoned(endPeriod.value) : null}
                onChange={onEndDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
              <TextField
                type="time"
                value={toTimeString(endPeriod?.value)}
                onChange={onEndTimeChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {periodSummaryText !== null && (
        <Box className={classes.hoursSummaryBox}>
          <Typography variant="h5">
            Are you sure you want to close during the time below?
          </Typography>
          <Typography variant="h3" sx={{ padding: '20px' }}>
            {periodSummaryText}
          </Typography>
        </Box>
      )}
      <Box className={classes.hoursActionButtonsBox}>
        <Box className={classes.hoursActionButtonBox}>
          <Button
            data-test-id="DHEs-zrfbBddIC0rjvHU1"
            variant="outlined"
            onClick={onClose}
            className={classes.actionButton}
          >
            Cancel
          </Button>
        </Box>
        <Box className={classes.hoursActionButtonBox}>
          <Button
            data-test-id="D0V6VIH1KL5JjSvks4V_D"
            variant="contained"
            onClick={() => {
              onCreate({ start: startPeriod.value, end: endPeriod.value });
            }}
            className={classes.actionButton}
            disabled={periodSummaryText === null}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

TempClosingHoursAddNew.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  timezone: PropTypes.string.isRequired,
};

export default TempClosingHoursAddNew;
